import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import App from "./App";
import { ApiError } from "@/lib/api";
import { authClient } from "@/lib/auth";
import "leaflet/dist/leaflet.css";
import "./index.css";

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      if (!(err instanceof ApiError)) return;
      if (err.status === 401) {
        // The session expired: make better-auth re-fetch it, so App redirects to /login.
        authClient.$store.notify("$sessionSignal");
      } else if (err.status === 403) {
        // Admin access was revoked: re-run the probe so App shows "Not an admin".
        void queryClient.invalidateQueries({ queryKey: ["admin-probe"] });
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: (count, error) => {
        // Never retry 4xx (auth/forbidden/not-found)
        if (error && typeof error === "object" && "status" in error) {
          const s = (error as { status?: number }).status ?? 0;
          if (s >= 400 && s < 500) return false;
        }
        return count < 2;
      },
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={150}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
        <Toaster
          position="bottom-right"
          theme="dark"
          richColors
          toastOptions={{
            style: {
              background: "hsl(222 47% 8%)",
              color: "hsl(210 40% 98%)",
              border: "1px solid hsl(217 32% 18%)",
            },
          }}
        />
      </TooltipProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
